/** Ephemeral microphone level display. No recording, persistence, playback, or network upload. */
export const VOICE_METER_BARS = 36;
function browserEnvironment() {
    if (typeof window === 'undefined')
        return {};
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    return {
        getUserMedia: typeof navigator !== 'undefined' && navigator.mediaDevices?.getUserMedia ? constraints => navigator.mediaDevices.getUserMedia(constraints) : undefined,
        createAudioContext: AudioContextClass ? () => new AudioContextClass() : undefined,
        requestFrame: window.requestAnimationFrame?.bind(window),
        cancelFrame: window.cancelAnimationFrame?.bind(window),
        document: typeof document === 'undefined' ? undefined : document,
        window,
    };
}
/** Call directly from the microphone click/hotkey so context resume and permission keep its gesture. */
export function startVoiceMeter(options) {
    const env = options.environment ?? browserEnvironment();
    let ended = false, stream, context;
    let source, analyser, frame;
    let levels = Array(VOICE_METER_BARS).fill(0);
    const publish = (status) => options.onUpdate({ status, levels: [...levels] });
    const stopTracks = (value) => {
        for (const track of value.getTracks()) {
            track.removeEventListener('ended', microphoneEnded);
            try {
                track.stop();
            }
            catch { /* Continue releasing the other tracks. */ }
        }
    };
    const release = () => {
        if (ended)
            return;
        ended = true;
        if (frame !== undefined)
            env.cancelFrame?.(frame);
        frame = undefined;
        env.document?.removeEventListener('visibilitychange', visibilityChanged);
        env.window?.removeEventListener('pagehide', pageHidden);
        if (stream)
            stopTracks(stream);
        stream = undefined;
        try {
            source?.disconnect();
        }
        catch { /* Already disconnected. */ }
        try {
            analyser?.disconnect();
        }
        catch { /* Already disconnected. */ }
        source = undefined;
        analyser = undefined;
        if (context) {
            context.removeEventListener('statechange', contextChanged);
            try {
                void context.close().catch(() => undefined);
            }
            catch { /* Already closed. */ }
            context = undefined;
        }
        levels = Array(VOICE_METER_BARS).fill(0);
    };
    const finish = (status) => {
        if (ended)
            return;
        release();
        publish(status);
    };
    const microphoneEnded = () => finish('unavailable');
    const visibilityChanged = () => { if (env.document?.hidden)
        finish('stopped'); };
    const pageHidden = () => finish('stopped');
    const contextChanged = () => { if (context && context.state !== 'running')
        finish('unavailable'); };
    const handle = { stop: release };
    if (env.document?.hidden) {
        finish('stopped');
        return handle;
    }
    if (!env.getUserMedia || !env.createAudioContext || !env.requestFrame || !env.cancelFrame) {
        finish('unavailable');
        return handle;
    }
    publish('starting');
    env.document?.addEventListener('visibilitychange', visibilityChanged);
    env.window?.addEventListener('pagehide', pageHidden);
    try {
        context = env.createAudioContext();
        // Start both operations synchronously; do not wait for a permission prompt before resuming audio.
        const resumed = context.state === 'running' ? Promise.resolve() : context.resume();
        void resumed.catch(() => finish('unavailable'));
        const requested = env.getUserMedia({ audio: true });
        void requested.then(value => {
            if (ended) {
                stopTracks(value);
                return;
            }
            stream = value;
            const tracks = stream.getAudioTracks();
            if (!tracks.length || tracks.every(track => track.readyState === 'ended')) {
                finish('unavailable');
                return;
            }
            for (const track of stream.getTracks())
                track.addEventListener('ended', microphoneEnded);
            void resumed.then(() => {
                if (ended || !context || !stream)
                    return;
                if (context.state !== 'running') {
                    finish('unavailable');
                    return;
                }
                analyser = context.createAnalyser();
                analyser.fftSize = 512;
                source = context.createMediaStreamSource(stream);
                source.connect(analyser); // Intentionally never connect to the speaker destination.
                context.addEventListener('statechange', contextChanged);
                const samples = new Uint8Array(analyser.fftSize);
                let lastSample = -Infinity;
                publish('live');
                const tick = now => {
                    frame = undefined;
                    if (ended || !analyser)
                        return;
                    if (env.document?.hidden) {
                        finish('stopped');
                        return;
                    }
                    try {
                        if (now - lastSample >= 80) {
                            analyser.getByteTimeDomainData(samples);
                            let squared = 0;
                            for (const sample of samples) {
                                const value = (sample - 128) / 128;
                                squared += value * value;
                            }
                            const amplitude = Math.min(1, Math.sqrt(squared / samples.length) * 5);
                            levels = [...levels.slice(1), amplitude];
                            lastSample = now;
                            publish('live');
                        }
                        if (!ended)
                            frame = env.requestFrame(tick);
                    }
                    catch {
                        finish('unavailable');
                    }
                };
                if (!ended)
                    frame = env.requestFrame(tick);
            }).catch(() => finish('unavailable'));
        }).catch(() => finish('unavailable'));
    }
    catch {
        finish('unavailable');
    }
    return handle;
}
//# sourceMappingURL=voiceMeter.js.map